import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Mail, Trash2, UserPlus, ShieldCheck, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/** Super-admin only — review inbound emails that didn't match a tenant
 *  (no `+token+scope` or token didn't resolve). Admin can assign to a
 *  tenant+customer OR delete. */
export default function AdminUnmatchedEmails() {
  const [state, setState] = useState({ loading: true, items: [], error: null });
  const [assignFor, setAssignFor] = useState(null); // email row
  const [deleteFor, setDeleteFor] = useState(null); // email row
  const [form, setForm] = useState({ business_id: "", customer_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get("/admin/emails/orphans");
      setState({ loading: false, items: data?.items || [], error: null });
    } catch (e) {
      setState({ loading: false, items: [], error: e.response?.data?.detail || "Failed to load" });
    }
  };
  useEffect(() => { load(); }, []);

  const openAssign = (row) => {
    setForm({ business_id: "", customer_id: "" });
    setAssignFor(row);
  };

  const doAssign = async () => {
    if (!assignFor) return;
    if (!form.business_id.trim()) { toast.error("Enter a business_id"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/emails/orphans/${assignFor.id}/assign`, {
        business_id: form.business_id.trim(),
        customer_id: form.customer_id.trim() || null,
      });
      toast.success("Email assigned");
      setAssignFor(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    setBusy(true);
    try {
      await api.delete(`/admin/emails/orphans/${deleteFor.id}`);
      toast.success("Email deleted");
      setDeleteFor(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  if (state.loading) {
    return <div className="p-6 flex items-center gap-2 text-zinc-500" data-testid="unmatched-emails-loading">
      <Loader2 className="w-4 h-4 animate-spin"/> Loading…
    </div>;
  }
  if (state.error) {
    return <div className="p-6 max-w-xl">
      <div className="bg-white border border-red-200 rounded-xl p-6 card-crisp">
        <div className="flex items-center gap-2 text-red-700"><AlertCircle className="w-5 h-5"/> <b>{state.error}</b></div>
      </div>
    </div>;
  }

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-6xl" data-testid="unmatched-emails-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold" data-testid="unmatched-emails-heading">Unmatched emails</h1>
          <p className="text-sm text-zinc-500">
            Inbound emails that couldn't be routed to a tenant. Review each and
            assign to the right business + customer, or delete.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <ShieldCheck className="w-4 h-4 text-emerald-600"/> Platform admin only
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="unmatched-emails-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
          </Button>
        </div>
      </div>

      {state.items.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-xl p-12 text-center text-zinc-500" data-testid="unmatched-emails-empty">
          <Mail className="w-8 h-8 mx-auto text-zinc-300 mb-2"/>
          No unmatched emails — everything is routed to a tenant.
        </div>
      ) : (
        <div className="space-y-3">
          {state.items.map((r) => (
            <div key={r.id} className="bg-white border border-zinc-200 rounded-xl p-5 card-crisp" data-testid={`unmatched-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-mono-accent uppercase tracking-widest">
                      Unassigned
                    </span>
                    <div className="font-medium text-sm truncate">
                      {r.subject || <em className="text-zinc-400">(no subject)</em>}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm text-zinc-600">
                    <div className="inline-flex items-center gap-2 min-w-0">
                      <span className="text-[10px] text-zinc-400 font-mono-accent uppercase tracking-widest shrink-0">From</span>
                      <span className="truncate">{r.from_email || "(unknown)"}</span>
                    </div>
                    <div className="inline-flex items-center gap-2 min-w-0">
                      <span className="text-[10px] text-zinc-400 font-mono-accent uppercase tracking-widest shrink-0">To</span>
                      <span className="truncate">{r.to_email || "(unknown)"}</span>
                    </div>
                    <div className="inline-flex items-center gap-2">
                      <span className="text-[10px] text-zinc-400 font-mono-accent uppercase tracking-widest">Received</span>
                      <span>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
                    </div>
                    <div className="inline-flex items-center gap-2 min-w-0">
                      <span className="text-[10px] text-zinc-400 font-mono-accent uppercase tracking-widest shrink-0">Email id</span>
                      <code className="text-xs font-mono-accent truncate">{r.id}</code>
                    </div>
                  </div>
                  {(r.body || "").trim() && (
                    <div className="mt-3 pl-3 border-l-2 border-zinc-200 text-sm text-zinc-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {(r.body || "").slice(0, 500)}
                      {(r.body || "").length > 500 && "…"}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteFor(r)}
                    className="text-red-700 border-red-300 hover:bg-red-50"
                    data-testid={`unmatched-delete-${r.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1"/> Delete
                  </Button>
                  <Button
                    onClick={() => openAssign(r)}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid={`unmatched-assign-${r.id}`}
                  >
                    <UserPlus className="w-4 h-4 mr-1"/> Assign…
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => { if (!o) setAssignFor(null); }}>
        <DialogContent data-testid="unmatched-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign email to a tenant</DialogTitle>
            <DialogDescription>
              This moves the email into the chosen workspace. The customer_id is optional — leave blank if unknown.
            </DialogDescription>
          </DialogHeader>
          {assignFor && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500">
                <div><span className="font-mono-accent uppercase tracking-widest text-[10px]">Subject</span>: {assignFor.subject || "(none)"}</div>
                <div><span className="font-mono-accent uppercase tracking-widest text-[10px]">From</span>: {assignFor.from_email || "(unknown)"}</div>
              </div>
              <div>
                <Label>Business ID</Label>
                <Input
                  value={form.business_id}
                  onChange={(e) => setForm({ ...form, business_id: e.target.value })}
                  placeholder="e.g. 6a5e503a096a6ac21a4f7557"
                  data-testid="unmatched-assign-business-id"
                  autoFocus
                />
              </div>
              <div>
                <Label>Customer ID <span className="text-zinc-400 text-xs">(optional)</span></Label>
                <Input
                  value={form.customer_id}
                  onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                  placeholder="Leave blank if unknown"
                  data-testid="unmatched-assign-customer-id"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={doAssign}
              disabled={busy || !form.business_id.trim()}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="unmatched-assign-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Assign email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteFor} onOpenChange={(o) => { if (!o) setDeleteFor(null); }}>
        <DialogContent data-testid="unmatched-delete-dialog">
          <DialogHeader>
            <DialogTitle>Permanently delete email?</DialogTitle>
            <DialogDescription>
              This cannot be undone. The email record will be removed from the database.
            </DialogDescription>
          </DialogHeader>
          {deleteFor && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-900 space-y-1">
              <div><strong>Subject:</strong> {deleteFor.subject || "(no subject)"}</div>
              <div><strong>From:</strong> {deleteFor.from_email || "(unknown)"}</div>
              <div><strong>Received:</strong> {deleteFor.created_at ? new Date(deleteFor.created_at).toLocaleString() : "—"}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFor(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={doDelete}
              disabled={busy}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="unmatched-delete-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : (<><Trash2 className="w-4 h-4 mr-1"/>Delete permanently</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
